'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { type FC, useEffect, useRef, useState } from 'react';

import Icon from 'molecules/Icon';
import Link from 'molecules/Link';

import styles from 'components/ListingHeader/listingHeader.module.scss';
import SearchBox from 'components/ListingHeader/searchBox';
import SearchDropdown from 'components/SearchDropdown';

import useRefOpenState from 'utils/hooks/useRefOpenState';
import { useSearchAutocomplete } from 'utils/hooks/useSearchAutocomplete';
import { useIsDesktop } from 'utils/useIsDesktop';

import type { SearchBoxProps } from 'components/ListingHeader/searchBox';

interface ExtendedSearchBoxProps extends SearchBoxProps {
  onSearchTrigger?: () => void;
}

const learningCenterDomain = process.env.NEXT_PUBLIC_LEARNING_CENTER_SITE_DOMAIN || '';

const ListingHeader: FC<ExtendedSearchBoxProps> = ({ loading, filteredItems, search = '', onSearchTrigger }) => {
  const router = useRouter();
  const pathname = usePathname();
  const isDesktop = useIsDesktop(768);
  const searchParams = useSearchParams();
  const currentParamsStringified = new URLSearchParams(searchParams.toString());
  const searchParam = currentParamsStringified.get('search') || '';
  const [text, setText] = useState(searchParam);
  const { ref, open, setOpen } = useRefOpenState<HTMLDivElement>(false, false);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasUserInteracted = useRef(false);
  const {
    results: liveResults,
    loading: liveLoading,
    inputValue,
    setInputValue,
    clearResults,
    isOpen,
  } = useSearchAutocomplete();
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const handleFocus = () => {
    if (hasUserInteracted.current) {
      setOpen(true);
    }
  };

  useEffect(() => {
    const syncSearchParam = async () => {
      if (searchParam === '' && text !== '') {
        setText('');
        setInputValue('');
        clearResults();
        setHasSubmitted(false);
      }
    };
    syncSearchParam();
  }, [searchParam]);

  const handleSearch = () => {
    setOpen(false);
    setHasSubmitted(true);
    clearResults();
    const searchString = text;

    if (searchString) {
      const params = new URLSearchParams(searchParams);
      params.set('search', searchString.toString());
      params.set('page', '1');
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    } else {
      router.replace(pathname, { scroll: false });
    }
    onSearchTrigger?.();
  };

  const handleTextChange = (value: string) => {
    setText(value);
    setInputValue(value);
    setHasSubmitted(false);
    setOpen(true);
  };

  return (
    <section className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerInner}>
          <h1>Welcome to Healthcare Insights</h1>
          <h2 className="heading5">
            Access expert guidance on the latest healthcare trends, industry developments and best practices.
          </h2>
          <h2 className="heading6">
            If you are a PolicyOwner looking for our Learning Center that includes CME opportunities and MagMutual risk
            management reports,{' '}
            <Link
              className={styles.link}
              href={`${learningCenterDomain}/content-library`}
              target="_self"
              variant="inline"
            >
              log in to MyMagMutual here
            </Link>
            .
          </h2>
          <div className={styles.searchBox} role="search">
            <div ref={ref} className={styles.search}>
              <div className={styles.formWrapper} id="search">
                <input
                  ref={inputRef}
                  type="text"
                  name="search"
                  id="search"
                  placeholder="eg. HIPAA, MACRA"
                  aria-label="Search for an entry"
                  className={styles.textField}
                  value={text}
                  onFocus={() => {
                    handleFocus();
                  }}
                  onClick={() => {
                    hasUserInteracted.current = true;
                    setOpen(true);
                  }}
                  onChange={e => {
                    handleTextChange(e.target.value);
                  }}
                  onKeyDown={e => {
                    const target = e.target as HTMLElement;
                    if (target.tagName === 'INPUT' && e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
                <button className={styles.searchButton} onClick={handleSearch} aria-label="Submit search">
                  <Icon icon="icon-search" size={42} className={styles.iconColor} />
                </button>
              </div>
              {open === true && isDesktop && !hasSubmitted && (
                <SearchDropdown
                  key={inputValue}
                  results={liveResults}
                  loading={liveLoading}
                  inputValue={inputValue}
                  isOpen={isOpen}
                  variant="hero"
                />
              )}
              {open === true && isDesktop && hasSubmitted && (
                <SearchBox loading={loading} filteredItems={filteredItems} search={search || ''} />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ListingHeader;
